import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, Video, MessageCircle, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MentorSession {
  id: string;
  mentorName: string;
  mentorTitle: string;
  mentorImage: string;
  expertise: string[];
  date: string;
  time: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  sessionType: 'video' | 'phone' | 'chat';
  notes?: string;
}

interface Mentor {
  id: string;
  name: string;
  title: string;
  company: string;
  expertise: string[];
  rating: number;
  image: string;
  available: boolean;
}

const MonthlyMentorHours: React.FC = () => {
  const { toast } = useToast();
  const [sessions, setSessions] = useState<MentorSession[]>([]);
  const [availableMentors] = useState<Mentor[]>([]);
  const [selectedTab, setSelectedTab] = useState<'sessions' | 'book' | 'mentors'>('sessions');

  const bookMentorSession = (mentorId: string) => {
    const mentor = availableMentors.find(m => m.id === mentorId);
    if (!mentor) return;

    const newSession: MentorSession = {
      id: Date.now().toString(),
      mentorName: mentor.name,
      mentorTitle: mentor.title,
      mentorImage: mentor.image,
      expertise: mentor.expertise,
      date: '2024-02-01',
      time: '15:00',
      duration: 60,
      status: 'scheduled',
      sessionType: 'video'
    };

    setSessions([newSession, ...sessions]);
    toast({
      title: "Session Booked!",
      description: `Your mentorship session with ${mentor.name} has been scheduled.`
    });
  };

  const cancelSession = (sessionId: string) => {
    setSessions(sessions.map(s => 
      s.id === sessionId ? { ...s, status: 'cancelled' as const } : s
    ));
    toast({
      title: "Session Cancelled",
      description: "Your mentorship session has been cancelled."
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSessionTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'phone': return <Clock className="h-4 w-4" />;
      case 'chat': return <MessageCircle className="h-4 w-4" />;
      default: return <Video className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-indigo-500" />
            1:1 Monthly Mentor Power Hours
          </CardTitle>
          <CardDescription>
            Connect with industry experts for personalized guidance and career development.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tab Navigation */}
          <div className="flex space-x-2 border-b">
            <button
              onClick={() => setSelectedTab('sessions')}
              className={`pb-2 px-1 ${selectedTab === 'sessions' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-muted-foreground'}`}
            >
              My Sessions
            </button>
            <button
              onClick={() => setSelectedTab('book')}
              className={`pb-2 px-1 ${selectedTab === 'book' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-muted-foreground'}`}
            >
              Book Session
            </button>
            <button
              onClick={() => setSelectedTab('mentors')}
              className={`pb-2 px-1 ${selectedTab === 'mentors' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-muted-foreground'}`}
            >
              Browse Mentors
            </button>
          </div>

          {/* My Sessions Tab */}
          {selectedTab === 'sessions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Your Mentorship Sessions</h3>
                <Badge className="bg-indigo-100 text-indigo-800">
                  {sessions.filter(s => s.status === 'scheduled').length} Upcoming
                </Badge>
              </div>
              
              {sessions.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-16 w-16 mx-auto mb-4 text-indigo-500" />
                  <h3 className="text-xl font-semibold mb-2">No Mentorship Sessions Yet</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Book your first 1:1 mentorship session with industry experts to accelerate your career growth.
                  </p>
                  <Button 
                    onClick={() => setSelectedTab('mentors')}
                    className="bg-indigo-500 hover:bg-indigo-600"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Browse Available Mentors
                  </Button>
                </div>
              ) : (
                sessions.map((session) => (
                  <Card key={session.id} className="border-l-4 border-l-indigo-500">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-indigo-600" />
                          </div>
                          <div>
                            <h4 className="font-medium">{session.mentorName}</h4>
                            <p className="text-sm text-muted-foreground">{session.mentorTitle}</p>
                          </div>
                        </div>
                        <Badge className={getStatusColor(session.status)}>
                          {session.status}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{session.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{session.time} ({session.duration} min)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getSessionTypeIcon(session.sessionType)}
                          <span className="text-sm capitalize">{session.sessionType} Call</span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex flex-wrap gap-1">
                          {session.expertise.map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {session.notes && (
                        <div className="mb-3 p-2 bg-gray-50 rounded text-sm">
                          <strong>Session Notes:</strong> {session.notes}
                        </div>
                      )}

                      <div className="flex gap-2">
                        {session.status === 'scheduled' && (
                          <>
                            <Button size="sm" className="bg-indigo-500 hover:bg-indigo-600">
                              Join Session
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => cancelSession(session.id)}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                        {session.status === 'completed' && (
                          <Button size="sm" variant="outline">
                            View Summary
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Browse Mentors Tab */}
          {selectedTab === 'mentors' && (
            <div className="space-y-4">
              <h3 className="font-semibold">Available Mentors</h3>
              
              {availableMentors.length === 0 ? (
                <div className="text-center py-12">
                  <User className="h-16 w-16 mx-auto mb-4 text-indigo-500" />
                  <h3 className="text-xl font-semibold mb-2">No Mentors Available</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Our mentor network is currently being expanded. Check back soon for available industry experts.
                  </p>
                  <Button className="bg-indigo-500 hover:bg-indigo-600">
                    Get Notified When Available
                  </Button>
                </div>
              ) : (
                availableMentors.map((mentor) => (
                  <Card key={mentor.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                            <User className="h-6 w-6 text-indigo-600" />
                          </div>
                          <div>
                            <h4 className="font-medium">{mentor.name}</h4>
                            <p className="text-sm text-muted-foreground">{mentor.title}</p>
                            <p className="text-sm text-muted-foreground">{mentor.company}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 mb-1">
                            <span className="text-sm">⭐ {mentor.rating}</span>
                          </div>
                          <Badge variant={mentor.available ? 'default' : 'secondary'}>
                            {mentor.available ? 'Available' : 'Busy'}
                          </Badge>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex flex-wrap gap-1">
                          {mentor.expertise.map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <Button 
                        onClick={() => bookMentorSession(mentor.id)}
                        disabled={!mentor.available}
                        className="bg-indigo-500 hover:bg-indigo-600"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Book Session
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Book Session Tab */}
          {selectedTab === 'book' && (
            <div className="space-y-4">
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-indigo-500" />
                <h3 className="font-semibold mb-2">Schedule Your Next Session</h3>
                <p className="text-muted-foreground mb-4">
                  Choose from our available mentors and book your personalized mentorship session.
                </p>
                <Button 
                  onClick={() => setSelectedTab('mentors')}
                  className="bg-indigo-500 hover:bg-indigo-600"
                >
                  Browse Available Mentors
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MonthlyMentorHours;
